import { CalendarClock, Info, X } from 'lucide-react';
import * as React from 'react';
import { SimpleTooltip, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { WorkspaceState } from '@/lib/types/rome';
import { cn } from '@/lib/utils/cn';

/**
 * Whether a workspace has an active, displayable node-maintenance auto-pause:
 * a non-zero ``scheduledShutdownAt`` AND a state where the pause is still
 * pending (running, or starting back up). The single source of this rule so the
 * table indicator, the home-page card, and the stream banner never disagree
 * about when the maintenance warning is shown.
 */
export function hasActiveMaintenance(workspace: { scheduledShutdownAt?: number; state?: WorkspaceState }): boolean {
  return Boolean(
    workspace.scheduledShutdownAt &&
      (workspace.state === WorkspaceState.RUNNING || workspace.state === WorkspaceState.STARTING)
  );
}

/**
 * Format a scheduled-shutdown epoch (seconds) into an absolute clock time and a
 * relative countdown. ``isPast`` is true once the pause time has elapsed (the
 * workspace is auto-pausing imminently). Callers re-render on a timer (the
 * workspace table already ticks every 60s) so the countdown stays live.
 */
export function formatMaintenanceCountdown(scheduledShutdownAt: number): {
  absolute: string;
  relative: string;
  isPast: boolean;
} {
  const date = new Date(scheduledShutdownAt * 1000);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const absolute = isToday
    ? `${time}`
    : `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${time}`;

  const secondsLeft = Math.floor((scheduledShutdownAt * 1000 - now.getTime()) / 1000);
  if (secondsLeft <= 0) {
    return { absolute, relative: 'now', isPast: true };
  }

  const minutes = Math.floor(secondsLeft / 60);
  let relative: string;
  if (minutes < 1) {
    relative = 'in under a minute';
  } else if (minutes < 60) {
    relative = `in ${minutes} min`;
  } else {
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    relative = remMinutes > 0 ? `in ${hours}h ${remMinutes}m` : `in ${hours}h`;
  }
  return { absolute, relative, isPast: false };
}

/**
 * The shared message users see across the console for a scheduled auto-pause.
 * It does NOT name the reason ("host maintenance") — callers supply that label
 * (the banner prefix, the tooltip lead) so it isn't stated twice.
 */
function maintenanceSentence(absolute: string, relative: string, isPast: boolean): string {
  const lead = isPast
    ? 'This workspace is auto-pausing now.'
    : `This workspace will auto-pause at ${absolute} (${relative}).`;
  return `${lead} Your data is preserved — restart anytime to move it to a healthy node.`;
}

export interface MaintenanceIndicatorProps {
  /** Unix seconds when the workspace will auto-pause. */
  scheduledShutdownAt: number;
  className?: string;
}

/**
 * Compact amber indicator for a workspace row: a calendar-clock icon in an amber
 * pill, with the absolute time, live countdown, and full explanation in a
 * tooltip. Icon-only keeps the dense State column from overflowing into the next
 * column; the summary banner carries the spelled-out countdown. Must be rendered
 * inside a ``TooltipProvider`` (the workspace table supplies one).
 */
export function MaintenanceIndicator({ scheduledShutdownAt, className }: MaintenanceIndicatorProps) {
  const { absolute, relative, isPast } = formatMaintenanceCountdown(scheduledShutdownAt);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={`Scheduled to auto-pause ${isPast ? 'now' : relative}`}
          className={cn(
            'inline-flex size-5 shrink-0 cursor-help items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
            className
          )}
        >
          <CalendarClock className="h-3 w-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent sideOffset={6}>
        <p className="max-w-[260px] text-xs">
          Scheduled host maintenance — {maintenanceSentence(absolute, relative, isPast)}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

export interface MaintenanceBannerProps {
  /**
   * Workspaces with a pending future auto-pause. The banner shows the soonest
   * one; when more than one is affected it leads with the count.
   */
  workspaces: { id: string; name: string; scheduledShutdownAt: number }[];
  className?: string;
  /** When provided, renders a dismiss button that invokes this callback. */
  onDismiss?: () => void;
}

/**
 * Amber summary bar shown above the workspace table (and in the connected
 * workspace chrome) when one or more workspaces are scheduled to auto-pause for
 * node maintenance. Mirrors the StreamConnectionWarning amber-bar styling.
 */
export function MaintenanceBanner({ workspaces, className, onDismiss }: MaintenanceBannerProps) {
  if (workspaces.length === 0) return null;

  const soonest = workspaces.reduce((a, b) => (a.scheduledShutdownAt <= b.scheduledShutdownAt ? a : b));
  const { absolute, relative, isPast } = formatMaintenanceCountdown(soonest.scheduledShutdownAt);

  const message =
    workspaces.length === 1
      ? maintenanceSentence(absolute, relative, isPast)
      : `${workspaces.length} workspaces will auto-pause, the soonest (${soonest.name || soonest.id}) ${isPast ? 'now' : `at ${absolute} (${relative})`}. Your data is preserved — restart anytime to move them to healthy nodes.`;

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-xl border border-amber-500/20 bg-amber-500/10 px-3.5 py-2.5 dark:border-amber-500/20 dark:bg-amber-500/10',
        className
      )}
      role="status"
    >
      <CalendarClock
        aria-hidden="true"
        className="mt-px size-3.5 shrink-0 text-amber-600 dark:text-amber-400"
        strokeWidth={2}
      />
      <p className="font-medium text-[12px] text-amber-900 leading-snug dark:text-amber-200">
        <span className="font-semibold">Scheduled host maintenance:</span> {message}
      </p>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss maintenance notice"
          className="-mr-1 -mt-0.5 ml-1 flex size-5 shrink-0 items-center justify-center rounded text-amber-700/60 transition-colors hover:bg-amber-500/15 hover:text-amber-900 dark:text-amber-300/60 dark:hover:text-amber-100"
        >
          <X className="size-3.5" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

export interface UpcomingMaintenanceCardProps {
  /**
   * Workspaces with a pending future auto-pause (state RUNNING/STARTING and a
   * non-zero `scheduledShutdownAt`). The card renders nothing when empty.
   */
  workspaces: { id: string; name: string; scheduledShutdownAt: number }[];
  className?: string;
}

/**
 * Sidebar card listing each workspace with an upcoming node-maintenance
 * auto-pause and when it will happen. Conditional: returns null when no
 * workspace is affected, so it only occupies space when relevant. Lives above
 * Team/Usage on the home page. Ticks every 60s to keep the countdowns live.
 */
export function UpcomingMaintenanceCard({ workspaces, className }: UpcomingMaintenanceCardProps) {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  if (workspaces.length === 0) return null;

  const sorted = [...workspaces].sort((a, b) => a.scheduledShutdownAt - b.scheduledShutdownAt);

  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border border-amber-500/30 bg-amber-500/5 dark:border-amber-500/20 dark:bg-amber-500/10',
        className
      )}
    >
      <div className="flex items-center gap-2.5 border-amber-500/20 border-b px-4 py-3 dark:border-amber-500/20">
        <div className="flex size-7 items-center justify-center rounded-md bg-amber-500/15 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300">
          <CalendarClock className="size-3.5" />
        </div>
        <h3 className="font-medium text-amber-900 text-sm dark:text-amber-200">Upcoming Maintenance</h3>
        <SimpleTooltip
          content={
            <p className="max-w-[260px] leading-snug">
              Antioch needs to perform maintenance on the underlying infrastructure these workspaces run on. Each
              workspace will automatically shut down at the listed time — no action is needed, and anything saved to its
              disk is safe. You can optionally restart a workspace any time before then to move it to a new node and
              avoid the pause.
            </p>
          }
        >
          <button
            type="button"
            aria-label="About scheduled maintenance"
            className="ml-auto flex size-5 cursor-help items-center justify-center rounded-full text-amber-600/70 hover:text-amber-700 dark:text-amber-300/70 dark:hover:text-amber-200"
          >
            <Info className="size-3.5" />
          </button>
        </SimpleTooltip>
      </div>

      <div className="flex flex-col gap-3 px-4 py-4">
        {sorted.map((ws) => {
          const { absolute, relative, isPast } = formatMaintenanceCountdown(ws.scheduledShutdownAt);
          return (
            <div key={ws.id} className="flex flex-col gap-0.5">
              <span className="truncate font-medium text-gray-800 text-sm dark:text-gray-100">{ws.name}</span>
              <span className="text-amber-700 text-xs dark:text-amber-300">
                {isPast ? 'Auto-pausing now' : `Auto-pauses ${absolute} · ${relative}`}
              </span>
            </div>
          );
        })}
        <p className="border-amber-500/15 border-t pt-3 text-[11px] text-gray-500 leading-snug dark:text-gray-400">
          Your data is preserved — restart anytime to move to a healthy node.
        </p>
      </div>
    </div>
  );
}

export default MaintenanceBanner;
